import { Euler, Object3D, Vector3 } from "three";
import { RailPartOptions, defaultRailPartOptions } from "./RailPartOptions";
import { createBox } from "./utils";
import { Meter } from "../../Meter";
import { Point } from "../../Point";

export class RailPart extends Object3D {
  protected _width: number;
  protected _options: RailPartOptions;
  private _bottomMesh?: Object3D;
  private _startBaseMesh?: Object3D;
  private _endBaseMesh?: Object3D;
  private _startEdgeMesh?: Object3D;
  private _endEdgeMesh?: Object3D;
  private _railMeshes?: Object3D[] = [];
  private _meter?: Meter;

  constructor(width: number, options: Partial<RailPartOptions> = {}) {
    super();
    this._width = width;
    this._options = { ...defaultRailPartOptions, ...options };
    this.init();
  }

  private init() {
    this._startBaseMesh = this.createBaseMesh();
    this.add(this._startBaseMesh);

    this._endBaseMesh = this.createBaseMesh(-1);
    this._endBaseMesh.position.set(this._width, 0, 0);
    this.add(this._endBaseMesh);

    this._bottomMesh = this.createBottomMesh(this._width - 0.04);
    this._bottomMesh.position.set(0.02, this._options.bottomOffset, 0);
    this.add(this._bottomMesh);

    this._startEdgeMesh = this.createEdgeMesh();
    this._startEdgeMesh.position.set(0.007, this._options.edgeOffset, 0.0);
    this.add(this._startEdgeMesh);

    this._endEdgeMesh = this.createEdgeMesh();
    this._endEdgeMesh.position.set(
      this._width - 0.02,
      this._options.edgeOffset,
      0
    );
    this.add(this._endEdgeMesh);

    const sPoint = new Point();
    sPoint.point = new Vector3(-0.5 * this._width, 0, 0);

    const ePoint = new Point();
    ePoint.point = new Vector3(0.5 * this._width, 0, 0);
    const mete = new Meter(undefined, sPoint, ePoint, {
      offset: new Vector3(0.0, 0.0, 0.0),
      rotation: new Euler(0, Math.PI, 0),
      minScale: 0.75,
      maxScale: 0.75,
      debug: false,
    });
    mete.render();
    mete.root.position.setY(this._options.height + 0.1);
    mete.root.position.setX(this._width * 0.5);
    this.add(mete.root);
    this._meter = mete;

    this.createRailMeshes();
  }

  protected createRailMeshes() {
    let s = 0.02;
    let rail: Object3D;
    while (s + this._options.railSpacing < this._width) {
      s += this._options.railSpacing;
      rail = this.createRailMesh();
      rail.position.setX(s);
      rail.position.setY(this._options.bottomOffset + this._options.railOffset);
      this._railMeshes?.push(rail);
      this.add(rail);
    }
  }

  protected createRailMesh() {
    return createBox({
      width: this._options.railWidth,
      height: this._options.railHeight,
      depth: this._options.railDepth,
    });
  }

  protected createEdgeMesh() {
    return createBox({
      width: this._options.edgeWidth,
      height: this._options.edgeHeight,
      depth: this._options.edgeDepth,
    });
  }

  protected createBottomMesh(width: number) {
    return createBox({
      width: width,
      height: this._options.bottomHeight,
      depth: this._options.bottomDepth,
    });
  }

  protected createBaseMesh(scale: number = 1) {
    const mesh = createBox({
      width: this._options.baseWidth,
      height: this._options.baseHeight,
      depth: this._options.baseDepth,
      offsetX: this._options.baseOffsetX,
      offsetY: this._options.baseOffsetY,
      offsetZ: this._options.baseOffsetZ,
      rotation: this._options.baseRotation,
    });

    mesh.scale.setX(scale);
    return mesh;
  }

  dispose() {
    this._bottomMesh?.removeFromParent();
    this._startBaseMesh?.removeFromParent();
    this._endBaseMesh?.removeFromParent();
    this._startEdgeMesh?.removeFromParent();
    this._endEdgeMesh?.removeFromParent();
    this._railMeshes?.forEach((rail) => rail.removeFromParent());
    this._meter?.dispose();
  }
}
